import {Modal, Button, Container, Table, Spinner} from "react-bootstrap";
import {IconButton} from "@mui/material";
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import React, {useState, useEffect} from "react";

import {LoadingSpinner} from "../../ui/LoadingSpinner";
import {ErrorsDescription} from "../../data/ErrorsDescription";
import {MachineErrors_Archive} from "../../pages/machines/MachineErrors_Archive";
import {MachineDetail} from "../../pages/machines/MachineDetail";


/**
 * This object shows a list of machines from the perspective of 'thing-status-table'.
 * The list handles pagination buttons according to LastEvaluatedKey parameter returned from the last query or scan.
 * Pages list is dynamically built on continues reloading, for every new page added is store the LastEvaluatedKey
 * (last key in the list of returned results) and the ExclusiveStartKey (key before the first key of the current set).
 * This is an example of the schema used to store the pages:
 *
 *   +------+-------------------+------------------+
 *   | page | ExclusiveStartKey | LastEvaluatedKey |
 *   +------+-------------------+------------------+
 *   | 1    | <null>            | <ID 1>           |
 *   | 2    | <ID 1>            | <ID 2>           |
 *   | 3    | <ID 2>            | <null>           |
 *   +------+-------------------+------------------+
 *
 *  HANDLERS ON THE OUTER OBJECTS
 *  This list interacts with the outer object using function handlers called when the user interact with
 *  the page list buttons.
 *
 *  - loadMoreDataHandler(fromExclusiveStartKey) -> machinesData, lastEvaluatedKey, reloadingPageFlag:
 *       called when the user press on MORE button to load new data from
 *
 *
 * @param show - show flag set by the outer class
 * @param complexDataObject - a complex object containing the data to load with the following format
 *        {
 *              data: <items of 'thing-status-table' with the filter defined by the outer element.>,
 *              ExclusiveStartKey: (start key for this dataset)
 *              LastEvaluatedKey: (last key from query or scan)
 *              newData: true|false (true if result of loading more data)
 *        }
 *
 * @param data - items from thing-status-table with defined by the outer element
 * @param lastEvaluatedKey - last evaluated key, null if the dataset is finished
 * @param isLoading - true when loading is active
 * @param title - title of the table
 * @param loadMoreHandler - f(fromExclusiveStartKey)
 * @param onCloseHandler - called when click on the close button
 *
 * @returns {React.JSX.Element}
 * @constructor
 */

export const MachinesStatusList = ({show, data, lastEvaluatedKey, isLoading, title,
                                       loadMoreHandler, onCloseHandler, }) => {

    // configurable in the future
    const showErrorsArchive = true
    const [loadingMore, setLoadingMore] = useState(false);

    // list events and variabled
    const onModalClosed = () => {
        onCloseHandler();
    };

    useEffect(() => {
        // when changing the dataset assume loading more has finished
        setLoadingMore(false);
    }, [data]);

    const loadMoreData = () => {
        setLoadingMore(true);
        loadMoreHandler();  // call the outer element handler
    } ;


    // //////////////////////////////////////////////////////////////////// machine detail stuff ////
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailModalUniqueId, setDetailModalUniqueId] = useState();

    const showMachineDetail = (uniqueId) => {
        setDetailModalUniqueId(uniqueId);
        setShowDetailModal(true);
    };

    const detailModalClosed = () => {
        setShowDetailModal(false);
    }


    /// interface

    const _loadMoreElement = () => {
        if (!lastEvaluatedKey) return null;

        return (
            <div className="d-flex justify-content-center my-3">
                <Button
                    variant="outline-primary"
                    onClick={loadMoreData}
                    disabled={loadingMore}
                >
                    {loadingMore ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-2" />
                            Loading...
                        </>
                    ) : (
                        'Load more'
                    )}
                </Button>
            </div>
        )
    }



    return (
        <>
        <MachineDetail show={showDetailModal} onClose={detailModalClosed} uniqueId={detailModalUniqueId}/>

        <Modal show={show} onHide={onModalClosed} fullscreen centered>
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <LoadingSpinner loadingFlag={isLoading}>

                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>Machine ID</th>
                                <th>Active Errors</th>
                                {showErrorsArchive && <th>Errors Archive</th>}
                                <th>&nbsp;</th>
                            </tr>

                        </thead>
                        <tbody>


                        {data &&  data.map((machine, idx) => (
                            <tr>
                                <td>{machine["uniqueId"]}</td>
                                <td><ErrorsDescription errorMask1={machine["dmk_0201"]}/></td>
                                {showErrorsArchive &&
                                    <td><MachineErrors_Archive uniqueId={machine.uniqueId}/></td>
                                }
                                <td>
                                    <IconButton onClick={() => showMachineDetail(machine.uniqueId)}>
                                        <PlaylistPlayIcon/>
                                    </IconButton>
                                </td>
                            </tr>
                            ))}
                        </tbody>
                    </Table>
                </LoadingSpinner>

                {_loadMoreElement()}
            </Modal.Body>

        </Modal>
        </>
    )
};